import React from 'react';
import { useResizeDetector } from 'react-resize-detector';

/**
 * Higher-Order Component that provides size information to wrapped components
 * This is a React 19-compatible replacement for react-sizeme's withSize HOC
 *
 * @example
 * ```tsx
 * const MyComponent = ({ size }) => (
 *   <div>Width: {size.width}</div>
 * );
 *
 * export default withSize()(MyComponent);
 * ```
 */
export interface SizeInfo {
  width?: number;
  height?: number;
}

export interface SizeMeProps {
  size: SizeInfo;
  width?: number;
  height?: number;
}

type WithSizeConfig = {
  monitorHeight?: boolean;
  monitorWidth?: boolean;
  refreshRate?: number;
  refreshMode?: 'throttle' | 'debounce';
};

export const withSize = (config: WithSizeConfig = {}) => {
  const {
    monitorHeight = false,
    monitorWidth = true,
    refreshRate = 16,
    refreshMode = 'debounce',
  } = config;

  return <P extends object>(
    Component: React.ComponentType<P & SizeMeProps>
  ): React.FC<Omit<P, 'size'>> => {
    const WrappedComponent: React.FC<Omit<P, 'size'>> = props => {
      const { width, height, ref } = useResizeDetector({
        handleHeight: monitorHeight,
        handleWidth: monitorWidth,
        refreshRate,
        refreshMode,
      });

      const size: SizeInfo = {
        ...(monitorWidth && { width }),
        ...(monitorHeight && { height }),
      };

      return (
        <div ref={ref} style={{ width: '100%', height: '100%' }}>
          <Component {...(props as P)} size={size} />
        </div>
      );
    };

    WrappedComponent.displayName = `withSize(${Component.displayName || Component.name || 'Component'})`;

    return WrappedComponent;
  };
};

export default withSize;
